export interface HankAuthState {
  token: string | null;
  refreshToken: string | null;
  sid: string | null;
  sub: string | null;
  email: string | null;
  role: string | null;
  tenantId: string | null;
  aal: number;
  stepupUntil: number | null;
}

export const HANK_AUTH_STORAGE_KEY = "agentme-hank-auth";
export const HANK_POST_LOGIN_PATH_KEY = "agentme-hank-post-login-path";

interface HankAuthClaims {
  sid?: string;
  sub?: string;
  email?: string;
  role?: string;
  tenant_id?: string;
  aal?: number;
  stepup_until?: number;
}

interface HankAuthTokenPayload {
  token?: string;
  accessToken?: string;
  refreshToken?: string | null;
  claims?: HankAuthClaims;
}

export function getStoredAuth(): HankAuthState {
  try {
    const raw = sessionStorage.getItem(HANK_AUTH_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<HankAuthState>;
      return {
        token: parsed.token ?? null,
        refreshToken: parsed.refreshToken ?? null,
        sid: parsed.sid ?? null,
        sub: parsed.sub ?? null,
        email: parsed.email ?? null,
        role: parsed.role ?? null,
        tenantId: parsed.tenantId ?? null,
        aal: typeof parsed.aal === "number" ? parsed.aal : 0,
        stepupUntil: typeof parsed.stepupUntil === "number" ? parsed.stepupUntil : null,
      };
    }
  } catch {
    // ignore parse/storage errors
  }

  return {
    token: null,
    refreshToken: null,
    sid: null,
    sub: null,
    email: null,
    role: null,
    tenantId: null,
    aal: 0,
    stepupUntil: null,
  };
}

export function setStoredAuth(state: HankAuthState): void {
  sessionStorage.setItem(HANK_AUTH_STORAGE_KEY, JSON.stringify(state));
}

export function clearStoredAuth(): void {
  sessionStorage.removeItem(HANK_AUTH_STORAGE_KEY);
}

export function buildPostLoginPath(locationLike: {
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  const pathname = String(locationLike.pathname || "").trim() || "/";
  return `${pathname}${locationLike.search ?? ""}${locationLike.hash ?? ""}`;
}

export function rememberPostLoginPath(path: string): void {
  const trimmed = String(path || "").trim();
  if (!trimmed) return;
  sessionStorage.setItem(HANK_POST_LOGIN_PATH_KEY, trimmed);
}

export function consumePostLoginPath(): string | null {
  const value = sessionStorage.getItem(HANK_POST_LOGIN_PATH_KEY);
  if (value) {
    sessionStorage.removeItem(HANK_POST_LOGIN_PATH_KEY);
    return value;
  }
  return null;
}

function isPublicAuthRoute(url: string): boolean {
  return (
    url.startsWith("/api/auth/login/password/start") ||
    url.startsWith("/api/auth/login/google/start") ||
    url.startsWith("/api/auth/login/google/callback") ||
    url.startsWith("/api/auth/passkeys/") ||
    url.startsWith("/api/auth/totp/") ||
    url.startsWith("/api/auth/session/refresh") ||
    url.startsWith("/api/auth/refresh") ||
    url.startsWith("/api/auth/introspect")
  );
}

function resolveRequestPath(input: RequestInfo | URL): string {
  const raw = typeof input === "string" ? input : input instanceof URL ? input.pathname : input.url;
  try {
    return new URL(raw, window.location.href).pathname;
  } catch {
    return raw;
  }
}

function parseJwtClaims(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload.padEnd(Math.ceil(payload.length / 4) * 4, "=");
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function buildStoredAuthFromPayload(
  payload: HankAuthTokenPayload,
  fallback: HankAuthState = getStoredAuth(),
): HankAuthState | null {
  const token = payload.token ?? payload.accessToken;
  if (!token) return null;

  const parsedClaims = parseJwtClaims(token);
  const claims = payload.claims ?? {};
  return {
    token,
    refreshToken: payload.refreshToken ?? fallback.refreshToken,
    sid:
      claims.sid ??
      (typeof parsedClaims?.sid === "string" ? parsedClaims.sid : (fallback.sid ?? null)),
    sub:
      claims.sub ??
      (typeof parsedClaims?.sub === "string" ? parsedClaims.sub : (fallback.sub ?? null)),
    email:
      claims.email ??
      (typeof parsedClaims?.email === "string" ? parsedClaims.email : (fallback.email ?? null)),
    role:
      claims.role ??
      (typeof parsedClaims?.role === "string" ? parsedClaims.role : (fallback.role ?? null)),
    tenantId:
      claims.tenant_id ??
      (typeof parsedClaims?.tenant_id === "string"
        ? parsedClaims.tenant_id
        : (fallback.tenantId ?? null)),
    aal:
      typeof claims.aal === "number"
        ? claims.aal
        : typeof parsedClaims?.aal === "number"
          ? parsedClaims.aal
          : fallback.aal,
    stepupUntil:
      typeof claims.stepup_until === "number"
        ? claims.stepup_until
        : typeof parsedClaims?.stepup_until === "number"
          ? parsedClaims.stepup_until
          : fallback.stepupUntil,
  };
}

function shouldAttemptRefreshFrom401(url: string, response: Response): Promise<boolean> {
  if (url.startsWith("/api/auth/")) return Promise.resolve(false);

  return response
    .clone()
    .json()
    .then((body) => {
      const error = String((body as { error?: unknown }).error ?? "")
        .trim()
        .toLowerCase();
      const message = String((body as { message?: unknown }).message ?? "")
        .trim()
        .toLowerCase();
      const authHeader = String(response.headers.get("www-authenticate") ?? "").toLowerCase();

      if (error === "step_up_required") return false;
      if (authHeader.includes("invalid_token")) return true;

      return (
        error === "unauthorized" &&
        (message === "invalid or expired token" || message === "bearer token required")
      );
    })
    .catch(() => false);
}

function shouldClearSessionFrom401(url: string, response: Response): Promise<boolean> {
  if (
    url.startsWith("/api/auth/session/refresh") ||
    url.startsWith("/api/auth/refresh") ||
    url.startsWith("/api/auth/introspect")
  ) {
    return Promise.resolve(true);
  }
  if (url.startsWith("/api/auth/")) return Promise.resolve(false);

  return response
    .clone()
    .json()
    .then((body) => {
      const error = String((body as { error?: unknown }).error ?? "")
        .trim()
        .toLowerCase();

      if (error === "step_up_required") return false;
      return false;
    })
    .catch(() => false);
}

let authFetchInstalled = false;
let refreshInFlight: Promise<HankAuthState | null> | null = null;

function buildAuthenticatedRequest(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  token: string | null,
): Request {
  const baseHeaders = input instanceof Request ? new Headers(input.headers) : new Headers();
  const initHeaders = new Headers(init?.headers);
  initHeaders.forEach((value, key) => {
    baseHeaders.set(key, value);
  });

  const url =
    typeof input === "string" || input instanceof URL
      ? input
      : new Request(input, { headers: baseHeaders, ...init });

  if (token && !baseHeaders.has("Authorization")) {
    baseHeaders.set("Authorization", `Bearer ${token}`);
  }

  return new Request(url, { ...init, headers: baseHeaders });
}

async function refreshAuthSession(
  originalFetch: typeof window.fetch,
): Promise<HankAuthState | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const auth = getStoredAuth();
      if (!auth.refreshToken) return null;

      const response = await originalFetch("/api/auth/session/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: auth.refreshToken }),
      });
      if (!response.ok) return null;

      const payload = (await response.json().catch(() => null)) as HankAuthTokenPayload | null;
      if (!payload) return null;

      const nextAuth = buildStoredAuthFromPayload(payload, auth);
      if (!nextAuth) return null;
      setStoredAuth(nextAuth);
      return nextAuth;
    })().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

export function installAuthFetch(): void {
  if (authFetchInstalled) return;
  authFetchInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = resolveRequestPath(input);
    const auth = getStoredAuth();
    const protectedApiRoute = url.startsWith("/api/") && !isPublicAuthRoute(url);
    const request = protectedApiRoute
      ? buildAuthenticatedRequest(input, init, auth.token)
      : new Request(input, init);
    const response = await originalFetch(request);

    if (response.status === 401 && protectedApiRoute) {
      const shouldRefresh = await shouldAttemptRefreshFrom401(url, response);
      if (shouldRefresh) {
        const refreshedAuth = await refreshAuthSession(originalFetch);
        if (refreshedAuth?.token) {
          const retryResponse = await originalFetch(
            buildAuthenticatedRequest(input, init, refreshedAuth.token),
          );
          if (retryResponse.status !== 401) {
            return retryResponse;
          }
          const retryNeedsRefresh = await shouldAttemptRefreshFrom401(url, retryResponse);
          const retryShouldLogout = await shouldClearSessionFrom401(url, retryResponse);
          if (!retryNeedsRefresh && !retryShouldLogout) {
            return retryResponse;
          }
        }

        rememberPostLoginPath(buildPostLoginPath(window.location));
        clearStoredAuth();
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return response;
      }

      const shouldLogout = await shouldClearSessionFrom401(url, response);
      if (shouldLogout) {
        rememberPostLoginPath(buildPostLoginPath(window.location));
        clearStoredAuth();
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
    }

    return response;
  };
}
